import React, { type FC, type PropsWithChildren } from 'react'
// reason: naming comes from MUI, we are aware it can change but by that time we will look at their Typography component again :)
// eslint-disable-next-line camelcase
import { unstable_memoTheme, styled, type TypographyProps as MuiTypographyProps } from '@mui/material'
import { type Theme } from '@dhl-ui/theme'
import { typographyAll, typographyMobile } from '../../theme/typography/typography'
import { useIsMobileVariant } from '../../theme/hooks/use-is-mobile-variant'
import { type TypographyCommon, type TypographyVariant } from './types'

interface TypographyOwnerState {
  align: MuiTypographyProps['align']
  color: `text.${TypographyVariant}` | 'static.goGreen' | 'initial'
  noWrap: boolean
  variant: MuiTypographyProps['variant']
}

interface Internal {
  ownerState: TypographyOwnerState
  isMobile: boolean
}

const TypographyRoot = styled('span')<Internal>(
  unstable_memoTheme(({ theme }: { theme: Theme }) => ({
    margin: 0,
    variants: [
      {
        props: { variant: 'inherit' },
        style: {
          font: 'inherit',
          lineHeight: 'inherit',
          letterSpacing: 'inherit',
        },
      },

      // JIRA-#DS-53 remove when old theme is gone, NOT NOW!
      // When everyone is using new Theme we can just take the entry from our typography tokens
      // ...Object.entries(theme.typography)
      ...Object.entries(typographyAll)
        // reason: only using value
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .filter(([_, value]) => typeof value === 'object')
        .map(([variant, style]) => ({
          props: { variant },
          style: ({ isMobile }: Internal) => {
            if (isMobile) {
              return typographyMobile[variant as keyof typeof typographyMobile] || style
            }

            return style
          },
        })),

      /*
        ...Object.entries(theme.palette).map(([color]) => ({
          props: { color },
          style: { color: theme.palette[color].main },
        })),
        */

      /**
       * Text Colors @example text.primary, text.secondary, etc.
       * We are not supporting any direct Palette color at the moment maybe we add these later,
       * see commented block above for when we do!
       */
      ...Object.entries(theme.palette.text).map(([key]) => ({
        props: { color: `text.${key}` },
        // reason: FIXME later if possible
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        style: { color: (theme as any).palette.text[key] },
      })),

      // Static palette colors that should be available as Typography color tokens
      {
        props: { color: 'static.goGreen' },
        style: { color: theme.palette.static.goGreen },
      },

      // actually please use Ellipsis consumers :)
      {
        props: ({ ownerState }: Internal) => ownerState.noWrap,
        style: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      },
    ],
  }))
)

type HtmlTag = 'p' | 'span' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'button' | 'caption'

const variantMapping: Record<string, HtmlTag> = {
  inherit: 'p',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  subtitle1: 'h6',
  subtitle2: 'h6',
  body1: 'p',
  body2: 'p',
  caption1: 'span',
  caption2: 'span',
  //
  button: 'button',
  caption: 'span',
  overline: 'span',
}

export type BaseTypographyProps = PropsWithChildren<
  TypographyCommon & {
    variant?: TypographyOwnerState['variant']
    align?: TypographyOwnerState['align']
    color?: TypographyVariant | 'inherit'
  }
>

const getOwnerStateColor = (color: TypographyVariant | 'inherit' | 'initial'): TypographyOwnerState['color'] => {
  if (color === 'initial') {
    return 'initial'
  }
  if (color === 'goGreen') {
    return 'static.goGreen'
  }
  return `text.${color}`
}
/**
 * Our BaseTypography is not leaning on `theme.typography` but on the pointer to `typography` instead.
 * By not leaning on the `theme.typography` we can keep all OLD components using the typography 100% the same.
 * So we give all teams/components the time to adjust when all teams are using the DHL Design System. This saves
 * us making lots of copies of components :)
 *
 * **When you directly lean on this exported `Typography` component it should only because you have dynamic variants**
 * You should not be colouring the H1/H2/etc with the colouring overhere, when you do need to please ask UX team why its missing
 * in its core! :)
 */
export const BaseTypography: FC<BaseTypographyProps> = ({ children, sx, ...inProps }) => {
  const isMobileVariant = useIsMobileVariant()
  const { component, variant = 'body1', align = 'inherit', noWrap = false, color = 'initial', ...other } = inProps

  // Determine actual DOM element
  const Component = component || variantMapping[variant] || 'span'

  // How MUI creates its elements, we pass this to its internal styled setup
  const ownerState: TypographyOwnerState = {
    variant,
    align,
    noWrap,
    color: getOwnerStateColor(color),
  }

  return (
    // reason: FIXME fix this later
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <TypographyRoot as={Component as any} ownerState={ownerState} isMobile={isMobileVariant} sx={sx} {...other}>
      {children}
    </TypographyRoot>
  )
}
